import { useEffect, useState } from "react";
import { US_STATES } from "../../utils/usStates";
import { SPORTS } from "../../utils/sports";
import { PROGRAM_TYPES } from "../../utils/programTypes";
import { AUDIENCES } from "../../utils/audiences";
import { AGE_GROUPS } from "../../utils/ageGroups";
import { COMPETITION_LEVELS } from "../../utils/competitionLevels";

import api from "@api";
import "./DirectoryLeagues.css";

function DirectoryLeagues() {
  const [leagues, setLeagues] = useState([]);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingLeague, setEditingLeague] = useState(null);
  const [alertModal, setAlertModal] = useState(null);
  // { type: "success" | "error", message: string }
  const [sportFilter, setSportFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("asc");
  const [locationsInput, setLocationsInput] = useState("");
  const [claimFilter, setClaimFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [programs, setPrograms] = useState([]);
  const [openModalSection, setOpenModalSection] = useState("basic");
  const [activeProgramIndex, setActiveProgramIndex] = useState(0);
  const [openProgramPanel, setOpenProgramPanel] = useState(null);
  const [editingScheduleIndex, setEditingScheduleIndex] = useState(null);
  const [editingVenueIndex, setEditingVenueIndex] = useState(null);
  const [draggedProgramIndex, setDraggedProgramIndex] = useState(null);
  const [form, setForm] = useState({
    name: "",
    contact_name: "",
    country: "US",
    city: "",
    county: "",
    locations: [],
    venues: [],
    state: "",
    phone_number: "",
    register_url: "",
    register_label: "",
    directory_logo: null,
    gallery_images: [],
    existing_directory_logo: "",
    existing_gallery_images: [],
    website_url: "",
    instagram_url: "",
    facebook_url: "",
    youtube_url: "",
    twitter_url: "",
    email: "",
    notes: "",
    description: "",
    is_active: true,
    is_verified: false,
  });
  const [availableStates, setAvailableStates] = useState([]);
  const [availableSports, setAvailableSports] = useState([]);
  const [genderFilter, setGenderFilter] = useState("all");
  const [availableGenders, setAvailableGenders] = useState([]);
  const fetchLeagues = async () => {
    try {
      const res = await api.get("/api/admin/directory/leagues", {
        params: {
          page,
          limit: 10,
          search: search || undefined,
          sport: sportFilter !== "all" ? sportFilter : undefined,
          state: stateFilter !== "all" ? stateFilter : undefined,
          gender: genderFilter !== "all" ? genderFilter : undefined,
          claim: claimFilter !== "all" ? claimFilter : undefined,
          sort: sortOrder,
        },
        withCredentials: true,
      });

      setLeagues(res.data.data || []);
      setTotalPages(res.data.totalPages || 1);
      setTotalCount(res.data.total || 0);
      setAvailableStates(res.data.availableStates || []);
      setAvailableSports(res.data.availableSports || []);
      setAvailableGenders(res.data.availableGenders || []);
    } catch (err) {
      console.error("Failed to fetch directory leagues", err);
    }
  };
  const toggleModalSection = (section) => {
    setOpenModalSection((current) => (current === section ? null : section));
  };

  const toggleProgramPanel = (panel) => {
    setOpenProgramPanel((current) => (current === panel ? null : panel));
    setEditingScheduleIndex(null);
    setEditingVenueIndex(null);
  };

  useEffect(() => {
    setPage(1);
  }, [search, sportFilter, stateFilter, genderFilter, claimFilter, sortOrder]);

  useEffect(() => {
    fetchLeagues();
  }, [page, search, sportFilter, stateFilter, genderFilter, claimFilter, sortOrder]);

  const handleDeactivate = async (id) => {
    const confirm = window.confirm("Deactivate this league?");
    if (!confirm) return;

    await api.delete(`/api/admin/directory/leagues/${id}`, {
      withCredentials: true,
    });
    fetchLeagues();
  };


  const removeExistingGalleryImage = (indexToRemove) => {
    setForm((prev) => ({
      ...prev,
      existing_gallery_images: prev.existing_gallery_images.filter(
        (_, index) => index !== indexToRemove
      ),
    }));
  };

  const removeNewGalleryImage = (indexToRemove) => {
    setForm((prev) => ({
      ...prev,
      gallery_images: prev.gallery_images.filter(
        (_, index) => index !== indexToRemove
      ),
    }));
  };
  const removeExistingLogo = () => {
    setForm((prev) => ({
      ...prev,
      existing_directory_logo: "",
      directory_logo: null,
    }));
  };

  const removeNewLogo = () => {
    setForm((prev) => ({
      ...prev,
      directory_logo: null,
    }));
  };
  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 10);

    if (!digits) return "";

    if (digits.length < 4) {
      return `(${digits}`;
    }

    if (digits.length < 7) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }

    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handleUnclaim = async (leagueId) => {
    const confirm = window.confirm(
      "Are you sure you want to remove this claim? The league will become unclaimed."
    );
    if (!confirm) return;

    try {
      await api.post(
        `/api/directory-league-claims/${leagueId}/unclaim`,
        {},
        { withCredentials: true }
      );

      setAlertModal({
        type: "success",
        message: "League claim removed.",
      });

      fetchLeagues();
    } catch (err) {
      console.error(err);
      setAlertModal({
        type: "error",
        message: "Failed to remove claim.",
      });
    }
  };


  const resetFilters = () => {
    setSearch("");
    setSportFilter("all");
    setStateFilter("all");
    setGenderFilter("all");
    setSortOrder("asc");
    setClaimFilter("all");
  };
  const updateProgramField = (index, field, value) => {
    const next = [...programs];
    next[index] = { ...next[index], [field]: value };
    setPrograms(next);
  };

  const addProgram = () => {
    const newProgram = {
      sport: "",
      title: "",
      program_type: "",
      gender: [],
      competition_level: "",
      min_age: "",
      max_age: "",
      description: "",
      contact_name: "",
      email: "",
      phone_number: "",
      contact_note: "",
      schedule_details: [],
      season: "",
      price: "",
      registration_url: "",
      registration_status: "",
      registration_deadline: "",
      start_date: "",
      end_date: "",
      notes: "",
      venues: [{ name: "", map_url: "" }],
    };

    setPrograms((prev) => {
      const nextPrograms = [...prev, newProgram];

      setActiveProgramIndex(nextPrograms.length - 1);
      setOpenProgramPanel(null);
      setEditingScheduleIndex(null);
      setEditingVenueIndex(null);

      return nextPrograms;
    });
  };

  const deleteProgram = (index) => {
    const confirmed = window.confirm(
      "Remove this program? This change will not be saved until you click Save."
    );

    if (!confirmed) return;

    setPrograms((prev) => {
      const nextPrograms = prev.filter((_, i) => i !== index);

      setActiveProgramIndex((currentIndex) => {
        if (nextPrograms.length === 0) return 0;
        if (index === currentIndex) return Math.max(0, currentIndex - 1);
        if (index < currentIndex) return currentIndex - 1;
        return currentIndex;
      });

      return nextPrograms;
    });

    setOpenProgramPanel(null);
    setEditingScheduleIndex(null);
    setEditingVenueIndex(null);
  };



  const reorderPrograms = (fromIndex, toIndex) => {
    if (fromIndex === null || fromIndex === toIndex) return;

    setPrograms((prev) => {
      const nextPrograms = [...prev];
      const [movedProgram] = nextPrograms.splice(fromIndex, 1);

      nextPrograms.splice(toIndex, 0, movedProgram);

      return nextPrograms.map((program, index) => ({
        ...program,
        display_order: index,
      }));
    });

    setActiveProgramIndex(toIndex);
    setDraggedProgramIndex(null);
  };


  const activeProgram = programs[activeProgramIndex] || null;

  const getProgramSummary = (program) => {
    const sportLabel =
      SPORTS.find((s) => s.key === program.sport)?.label || "Sport";

    const typeLabel =
      PROGRAM_TYPES.find((p) => p.key === program.program_type)?.label ||
      "Program";

    const competitionLabel = COMPETITION_LEVELS.find(
      (c) => c.key === program.competition_level
    )?.label;

    const audienceLabel =
      (program.gender || [])
        .map((g) => AUDIENCES.find((a) => a.key === g)?.label)
        .filter(Boolean)
        .join(", ") || "Audience";

    const ageLabel = program.min_age
      ? program.max_age
        ? `${program.min_age}-${program.max_age}`
        : `${program.min_age}+`
      : "Age";

    return [
      sportLabel,
      typeLabel,
      competitionLabel,
      audienceLabel,
      ageLabel,
    ]
      .filter(Boolean)
      .join(" · ");
  };

  const formatScheduleSummary = (schedule) => {
    const days = Array.isArray(schedule.days)
      ? schedule.days.map((day) => day.slice(0, 3)).join(", ")
      : schedule.day
        ? schedule.day.slice(0, 3)
        : "Days";

    const time =
      schedule.start_time || schedule.end_time
        ? `${schedule.start_time || "Start"} - ${schedule.end_time || "End"}`
        : "Time";

    return `${days} · ${time}`;
  };

  const formatVenueSummary = (venue) => {
    if (!venue?.name) return "Untitled venue";

    return venue.name;
  };


  const normalizeCounty = (value = "") => {
    let county = value.trim();

    if (!county) return "";

    county = county
      .replace(/\s+/g, " ")
      .replace(/\bcounty\b\.?/gi, "")
      .replace(/\bco\.?\b/gi, "")
      .trim();

    if (!county) return "";

    county = county
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");

    if (county.toLowerCase().endsWith(" city")) {
      return county;
    }

    return `${county} County`;
  };


  const addListingVenue = () => {
    setForm((prev) => ({
      ...prev,
      venues: [...(prev.venues || []), { name: "", map_url: "" }],
    }));
  };

  const updateListingVenue = (index, field, value) => {
    setForm((prev) => {
      const nextVenues = [...(prev.venues || [])];

      nextVenues[index] = {
        ...nextVenues[index],
        [field]: value,
      };

      return {
        ...prev,
        venues: nextVenues,
      };
    });
  };

  const removeListingVenue = (indexToRemove) => {
    setForm((prev) => ({
      ...prev,
      venues: (prev.venues || []).filter((_, index) => index !== indexToRemove),
    }));
  };



  return (
    <div className="directory-admin-container">
      <div className="directory-admin-header">
        {alertModal && (
          <div className="directory-modal">
            <div className={`directory-modal-content admin-directory-alert-modal ${alertModal.type}`}>
              <h3 className="alert-title">
                {alertModal.type === "success" ? "Success" : "Error"}
              </h3>
              <p className="alert-message">{alertModal.message}</p>
              <div className="modal-actions">
                <button
                  className="btn-primary"
                  onClick={() => setAlertModal(null)}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        <h2>Directory Leagues</h2>
        <button
          className="btn-primary"
          onClick={() => {
            setEditingLeague(null); // ✅ ADD MODE

            setForm({
              name: "",
              contact_name: "",
              country: "US",
              city: "",
              state: "",
              county: "",
              locations: [],
              venues: [],
              phone_number: "",
              website_url: "",
              register_url: "",
              register_label: "",
              directory_logo: null,
              gallery_images: [],
              existing_directory_logo: "",
              existing_gallery_images: [],
              instagram_url: "",
              facebook_url: "",
              youtube_url: "",
              twitter_url: "",
              email: "",
              notes: "",
              description: "",
              is_active: true,
              is_verified: false,
            });
            setLocationsInput("");
            setPrograms([]);
            setActiveProgramIndex(0);
            setOpenModalSection("basic");
            setOpenProgramPanel(null);
            setEditingScheduleIndex(null);
            setEditingVenueIndex(null);
            setShowModal(true);
          }}
        >
          + Add League
        </button>

      </div>

      {/* Filters */}
      <div className="directory-filters">
        <input
          type="text"
          placeholder="Search by league name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          value={sportFilter}
          onChange={(e) => setSportFilter(e.target.value)}
        >
          <option value="all">All Sports</option>
          {availableSports.map((sportKey) => {
            const sport = SPORTS.find((s) => s.key === sportKey);
            return (
              <option key={sportKey} value={sportKey}>
                {sport?.label || sportKey}
              </option>
            );
          })}
        </select>
        <select
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
        >
          <option value="all">All States</option>
          {availableStates.map((code) => {
            const state = US_STATES.find((s) => s.code === code);
            return (
              <option key={code} value={code}>
                {state?.name || code}
              </option>
            );
          })}
        </select>
        <select
          value={genderFilter}
          onChange={(e) => setGenderFilter(e.target.value)}
        >
          <option value="all">All Audiences</option>
          {availableGenders.map((gender) => (
            <option key={gender} value={gender}>
              {gender === "male"
                ? "Male"
                : gender === "female"
                  ? "Female"
                  : gender === "coed"
                    ? "Coed"
                    : gender}
            </option>
          ))}
        </select>
        <select
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
        >
          <option value="asc">Name A → Z</option>
          <option value="desc">Name Z → A</option>
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
        </select>
        <select
          value={claimFilter}
          onChange={(e) => setClaimFilter(e.target.value)}
        >
          <option value="all">All Claims</option>
          <option value="claimed">Claimed</option>
          <option value="pending">Pending</option>
          <option value="unclaimed">Unclaimed</option>
        </select>
        <button className="btn-secondary" onClick={resetFilters}>
          Reset
        </button>
      </div>
      <div className="results-summary">
        Showing {leagues.length} of {totalCount} leagues
      </div>
      {/* Table */}
      <div className="directory-table-wrapper">
        <table className="directory-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Date Added</th>
              <th>Claimed</th>
              <th>Name</th>
              <th>Sports</th>
              <th>Country</th>
              <th>County</th>
              <th>City</th>
              <th>State</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {leagues.length === 0 && (
              <tr>
                <td colSpan="8" className="empty">
                  No directory leagues found
                </td>
              </tr>
            )}

            {leagues.map((l, index) => (
              <tr key={l.id}>
                <td>{(page - 1) * 10 + index + 1}</td>
                <td>
                  {new Date(l.createdAt).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </td>
                <td className="claim-cell">
                  {l.claimed_user_id ? (
                    <span title="Claimed">🟢</span>
                  ) : l.has_pending_claim ? (
                    <span
                      title="Pending Claim"
                      className="pending-link"
                      onClick={() => window.location.href = "/admin/directory-claims"}
                    >
                      🟡
                    </span>
                  ) : (
                    <span title="Unclaimed">⚪</span>
                  )}
                </td>



                <td>{l.name}</td>
                <td>
                  {[...new Set((l.programs || []).map(p => p.sport))].join(", ") || "—"}
                </td>
                <td>{l.country || "US"}</td>
                <td>{l.county || "-"}</td>
                <td>{l.city || "—"}</td>
                <td>{l.state || "—"}</td>
                <td className="actions">
                  <button
                    title="Edit"
                    onClick={async () => {
                      try {
                        const leagueRes = await api.get(`/api/directory/leagues/${l.id}`, {
                          withCredentials: true,
                        });

                        const league = leagueRes.data;

                        setEditingLeague(league);

                        setForm({
                          name: league.name || "",
                          contact_name: league.contact_name || "",
                          country: league.country || "US",
                          city: league.city || "",
                          county: league.county || "",
                          locations: league.locations || [],
                          venues: Array.isArray(league.venues) ? league.venues : [],
                          state: league.state || "",
                          register_url: league.register_url || "",
                          register_label: league.register_label || "",
                          directory_logo: null,
                          gallery_images: [],
                          existing_directory_logo: league.directory_logo || "",
                          existing_gallery_images: league.gallery_images || [],
                          phone_number: league.phone_number || "",
                          website_url: league.website_url || "",
                          instagram_url: league.instagram_url || "",
                          facebook_url: league.facebook_url || "",
                          youtube_url: league.youtube_url || "",
                          twitter_url: league.twitter_url || "",
                          email: league.email || "",
                          notes: league.notes || "",
                          description: league.description || "",
                          is_active: league.is_active,
                          is_verified: league.is_verified,
                        });

                        setLocationsInput((league.locations || []).join(", "));

                        const res = await api.get(`/api/admin/directory/leagues/${l.id}/programs`, {
                          withCredentials: true,
                        });

                        setPrograms(
                          (res.data || []).map((program) => {
                            let normalizedGender = [];

                            if (Array.isArray(program.gender)) {
                              normalizedGender = program.gender;
                            } else if (typeof program.gender === "string") {
                              const trimmed = program.gender.trim();

                              if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
                                normalizedGender = trimmed
                                  .slice(1, -1)
                                  .split(",")
                                  .map((g) => g.trim().replace(/^"|"$/g, ""))
                                  .filter(Boolean);
                              } else if (trimmed) {
                                normalizedGender = [trimmed];
                              }
                            }

                            return {
                              ...program,
                              gender: normalizedGender,
                              contact_note: program.contact_note || "",
                              schedule_details: Array.isArray(program.schedule_details)
                                ? program.schedule_details.map((schedule) => ({
                                  ...schedule,
                                  days: Array.isArray(schedule.days)
                                    ? schedule.days
                                    : schedule.day
                                      ? [schedule.day]
                                      : [],
                                }))
                                : [],
                              price: program.price || "",
                              registration_url: program.registration_url || "",
                              registration_status: program.registration_status || "",
                              registration_deadline: program.registration_deadline || "",
                              start_date: program.start_date || "",
                              end_date: program.end_date || "",
                              notes: program.notes || "",
                              venues: Array.isArray(program.venues) ? program.venues : [],
                            };
                          })
                        );
                        setActiveProgramIndex(0);
                        setOpenModalSection("basic");
                        setOpenProgramPanel(null);
                        setEditingScheduleIndex(null);
                        setEditingVenueIndex(null);
                        setShowModal(true);
                      } catch (err) {
                        console.error("Failed to fetch league details", err);
                        setAlertModal({
                          type: "error",
                          message: "Failed to load league details.",
                        });
                      }
                    }}



                  >
                    ✏️
                  </button>

                  {l.is_active && (
                    <button
                      title="Deactivate"
                      onClick={() => handleDeactivate(l.id)}
                    >
                      🗑️
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="pagination">
          <button
            className="page-btn"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            ← Prev
          </button>

          <span className="page-info">
            Page {page} of {totalPages}
          </span>

          <button
            className="page-btn"
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next →
          </button>
        </div>
      </div>




      {showModal && (
        <div className="directory-modal">
          <div className="directory-modal-content">
            <div className="admin-listing-modal-header">
              <div>
                <h3>{editingLeague ? "Edit League" : "Add League"}</h3>
                <p>Manage listing details, programs, media, and admin settings</p>
              </div>
            </div>


            {/* BASIC INFO */}
            <div className="admin-listing-card">
              <div
                className="admin-listing-card-header"
                onClick={() => toggleModalSection("basic")}
              >
                <div>
                  <h3>Basic Information</h3>
                  <p>League name, location, and description</p>
                </div>

                <span>{openModalSection === "basic" ? "▲" : "▼"}</span>
              </div>

              {openModalSection === "basic" && (
                <div className="admin-listing-card-body">
                  <div className="admin-field-group admin-full-width">
                    <label className="admin-field-label">League Name</label>
                    <input
                      type="text"
                      placeholder="League Name *"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                    />
                  </div>

                  <div className="admin-listing-row">
                    <div className="admin-field-group">
                      <label className="admin-field-label">County / Metro</label>
                      <input
                        type="text"
                        placeholder="Orange County"
                        value={form.county}
                        onChange={(e) => {
                          setForm({ ...form, county: e.target.value });
                        }}
                        onBlur={(e) => {
                          setForm((prev) => ({
                            ...prev,
                            county: normalizeCounty(e.target.value),
                          }));
                        }}
                      />
                    </div>

                    <div className="admin-field-group">
                      <label className="admin-field-label">City</label>
                      <input
                        type="text"
                        placeholder="City"
                        value={form.city}
                        onChange={(e) => setForm({ ...form, city: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="admin-field-group admin-full-width">
                    <label className="admin-field-label">Other Cities</label>
                    <input
                      type="text"
                      placeholder="Comma separated, ex: Irvine, Tustin, Orange"
                      value={locationsInput}
                      onChange={(e) => setLocationsInput(e.target.value)}
                      onBlur={() =>
                        setForm({
                          ...form,
                          locations: locationsInput
                            .split(",")
                            .map((c) => c.trim())
                            .filter(Boolean),
                        })
                      }
                    />
                  </div>

                  <div className="admin-listing-row">
                    <div className="admin-field-group">
                      <label className="admin-field-label">Country</label>
                      <select
                        value={form.country}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            country: e.target.value,
                            state: "",
                            county: "",
                          })
                        }
                      >
                        <option value="US">United States</option>
                        <option value="JP">Japan</option>
                        <option value="CA">Canada</option>
                      </select>
                    </div>

                    {form.country === "US" && (
                      <div className="admin-field-group">
                        <label className="admin-field-label">State</label>
                        <select
                          value={form.state}
                          onChange={(e) => setForm({ ...form, state: e.target.value })}
                        >
                          <option value="">Select State</option>
                          {US_STATES.map((s) => (
                            <option key={s.code} value={s.code}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                  <div className="admin-field-group admin-full-width">
                    <label className="admin-field-label">Main Venues / Locations</label>
                    <p className="admin-field-help">
                      Add the main facility or map locations shown on the public listing.
                    </p>

                    {(form.venues || []).map((venue, index) => (
                      <div key={index} className="admin-mini-card">
                        <div className="admin-field-group">
                          <label className="admin-field-label">Venue Name</label>
                          <input
                            type="text"
                            placeholder="Ex: Agape Tennis Academy"
                            value={venue.name || ""}
                            onChange={(e) =>
                              updateListingVenue(index, "name", e.target.value)
                            }
                          />
                        </div>

                        <div className="admin-field-group">
                          <label className="admin-field-label">Google Map URL</label>
                          <input
                            type="text"
                            placeholder="https://maps.google.com/..."
                            value={venue.map_url || ""}
                            onChange={(e) =>
                              updateListingVenue(index, "map_url", e.target.value)
                            }
                          />
                        </div>

                        <button
                          type="button"
                          className="admin-small-danger"
                          onClick={() => removeListingVenue(index)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}

                    <button
                      type="button"
                      className="btn-secondary directory-small-btn"
                      onClick={addListingVenue}
                    >
                      + Add Listing Venue
                    </button>
                  </div>
                  <div className="admin-field-group admin-full-width">
                    <label className="admin-field-label">Description</label>
                    <textarea
                      placeholder="Description of League"
                      value={form.description || ""}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                    />
                  </div>
                </div>
              )}
            </div>


            {/* CONTACT */}
            <div className="admin-listing-card">
              <div
                className="admin-listing-card-header"
                onClick={() => toggleModalSection("links")}
              >
                <div>
                  <h3>Website & Social</h3>
                  <p>Website, registration link, and social media</p>
                </div>

                <span>{openModalSection === "links" ? "▲" : "▼"}</span>
              </div>

              {openModalSection === "links" && (
                <div className="admin-listing-card-body">
                  <div className="admin-field-group admin-full-width">
                    <label className="admin-field-label">Website URL</label>
                    <input
                      type="text"
                      placeholder="https://..."
                      value={form.website_url || ""}
                      onChange={(e) =>
                        setForm({ ...form, website_url: e.target.value })
                      }
                    />
                  </div>

                  <div className="admin-listing-row">
                    <div className="admin-field-group">
                      <label className="admin-field-label">Register URL</label>
                      <input
                        type="text"
                        placeholder="https://..."
                        value={form.register_url || ""}
                        onChange={(e) =>
                          setForm({ ...form, register_url: e.target.value })
                        }
                      />
                    </div>

                    <div className="admin-field-group">
                      <label className="admin-field-label">Register Button Label</label>
                      <input
                        type="text"
                        placeholder="Ex: Register, Sign Up, Join League"
                        value={form.register_label || ""}
                        onChange={(e) =>
                          setForm({ ...form, register_label: e.target.value })
                        }
                      />
                    </div>
                  </div>

                  <div className="admin-listing-row">
                    <div className="admin-field-group">
                      <label className="admin-field-label">Instagram URL</label>
                      <input
                        type="text"
                        placeholder="Instagram URL"
                        value={form.instagram_url || ""}
                        onChange={(e) =>
                          setForm({ ...form, instagram_url: e.target.value })
                        }
                      />
                    </div>

                    <div className="admin-field-group">
                      <label className="admin-field-label">Facebook URL</label>
                      <input
                        type="text"
                        placeholder="Facebook URL"
                        value={form.facebook_url || ""}
                        onChange={(e) =>
                          setForm({ ...form, facebook_url: e.target.value })
                        }
                      />
                    </div>
                  </div>

                  <div className="admin-listing-row">
                    <div className="admin-field-group">
                      <label className="admin-field-label">YouTube URL</label>
                      <input
                        type="text"
                        placeholder="YouTube URL"
                        value={form.youtube_url || ""}
                        onChange={(e) =>
                          setForm({ ...form, youtube_url: e.target.value })
                        }
                      />
                    </div>

                    <div className="admin-field-group">
                      <label className="admin-field-label">Twitter / X URL</label>
                      <input
                        type="text"
                        placeholder="Twitter / X URL"
                        value={form.twitter_url || ""}
                        onChange={(e) =>
                          setForm({ ...form, twitter_url: e.target.value })
                        }
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* CONTACT */}
            <div className="admin-listing-card">
              <div
                className="admin-listing-card-header"
                onClick={() => toggleModalSection("contact")}
              >
                <div>
                  <h3>Contact</h3>
                  <p>Main contact person, email, and phone number</p>
                </div>

                <span>{openModalSection === "contact" ? "▲" : "▼"}</span>
              </div>

              {openModalSection === "contact" && (
                <div className="admin-listing-card-body">
                  <div className="admin-field-group">
                    <label className="admin-field-label">Contact Name</label>
                    <input
                      type="text"
                      placeholder="Contact Name"
                      value={form.contact_name || ""}
                      onChange={(e) =>
                        setForm({ ...form, contact_name: e.target.value })
                      }
                    />
                  </div>

                  <div className="admin-field-group">
                    <label className="admin-field-label">Contact Email</label>
                    <input
                      type="email"
                      placeholder="Contact Email"
                      value={form.email || ""}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                    />
                  </div>

                  <div className="admin-field-group">
                    <label className="admin-field-label">Phone Number</label>
                    <input
                      type="text"
                      placeholder="(123) 456-7890"
                      value={form.phone_number || ""}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          phone_number: formatPhone(e.target.value),
                        })
                      }
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="admin-listing-card">
              <div
                className="admin-listing-card-header"
                onClick={() => toggleModalSection("media")}
              >
                <div>
                  <h3>Media</h3>
                  <p>Directory logo and gallery images</p>
                </div>

                <span>{openModalSection === "media" ? "▲" : "▼"}</span>
              </div>

              {openModalSection === "media" && (
                <div className="admin-listing-card-body">
                  <div className="admin-field-group admin-full-width">
                    <label className="admin-field-label">Directory Logo</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) =>
                        setForm({
                          ...form,
                          directory_logo: e.target.files?.[0] || null,
                        })
                      }
                    />

                    {(form.existing_directory_logo || form.directory_logo) && (
                      <div className="directory-logo-preview-wrap">
                        <div className="directory-logo-preview">
                          <img
                            src={
                              form.directory_logo
                                ? URL.createObjectURL(form.directory_logo)
                                : form.existing_directory_logo
                            }
                            alt="Directory logo"
                          />
                          <button
                            type="button"
                            className="directory-preview-remove"
                            onClick={
                              form.directory_logo ? removeNewLogo : removeExistingLogo
                            }
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="admin-field-group admin-full-width">
                    <label className="admin-field-label">Gallery Images (max 5)</label>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => {
                        const newFiles = Array.from(e.target.files || []);

                        setForm((prev) => ({
                          ...prev,
                          gallery_images: [
                            ...prev.gallery_images,
                            ...newFiles,
                          ].slice(0, 5),
                        }));

                        e.target.value = "";
                      }}
                    />

                    {(form.existing_gallery_images?.length > 0 ||
                      form.gallery_images?.length > 0) && (
                        <div className="directory-gallery-grid">
                          {form.existing_gallery_images.map((img, index) => (
                            <div
                              key={`existing-${index}`}
                              className="directory-gallery-item"
                            >
                              <img src={img} alt={`Gallery ${index + 1}`} />
                              <button
                                type="button"
                                className="directory-preview-remove"
                                onClick={() => removeExistingGalleryImage(index)}
                              >
                                ×
                              </button>
                            </div>
                          ))}

                          {form.gallery_images.map((file, index) => (
                            <div
                              key={`new-${index}`}
                              className="directory-gallery-item"
                            >
                              <img
                                src={URL.createObjectURL(file)}
                                alt={`New gallery ${index + 1}`}
                              />
                              <button
                                type="button"
                                className="directory-preview-remove"
                                onClick={() => removeNewGalleryImage(index)}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                </div>
              )}
            </div>






            {/* ADMIN NOTES */}
            <div className="admin-listing-card">
              <div
                className="admin-listing-card-header"
                onClick={() => toggleModalSection("admin")}
              >
                <div>
                  <h3>Admin Settings</h3>
                  <p>Active status, verification, and internal notes</p>
                </div>
                <span>{openModalSection === "admin" ? "▲" : "▼"}</span>
              </div>
              {openModalSection === "admin" && (
                <div className="admin-listing-card-body">
                  <div className="flag-row">
                    <div
                      className={`flag-chip ${form.is_active ? "on" : "off"}`}
                      onClick={() => setForm({ ...form, is_active: !form.is_active })}
                    >
                      {form.is_active ? "🟢 Active" : "⚪ Inactive"}
                    </div>
                    <div
                      className={`flag-chip ${form.is_verified ? "verified" : ""}`}
                      onClick={() => setForm({ ...form, is_verified: !form.is_verified })}
                    >
                      {form.is_verified ? "✅ Verified" : "⬜ Not Verified"}
                    </div>
                  </div>
                  <div className="admin-field-group admin-full-width">
                    <label className="admin-field-label">Admin Notes</label>
                    <textarea
                      placeholder="Internal notes only. Users will not see this."
                      value={form.notes || ""}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    />
                  </div>
                </div>
              )}
            </div>


            {/* PROGRAMS */}
            <div className="admin-listing-card">
              <div
                className="admin-listing-card-header"
                onClick={() => toggleModalSection("programs")}
              >
                <div>
                  <h3>Programs</h3>
                  <p>Programs, classes, lessons, leagues, and events</p>
                </div>

                <span>{openModalSection === "programs" ? "▲" : "▼"}</span>
              </div>

              {openModalSection === "programs" && (
                <div className="admin-listing-card-body">
                  <div className="admin-program-layout">
                    <div className="admin-program-list">
                      <button type="button" className="btn-secondary" onClick={addProgram}>
                        + Add Program
                      </button>

                      {programs.map((program, index) => (
                        <div
                          key={program.id || index}
                          draggable
                          className={`admin-program-summary-card ${activeProgramIndex === index ? "active" : ""
                            } ${draggedProgramIndex === index ? "is-dragging" : ""}`}
                          onDragStart={() => setDraggedProgramIndex(index)}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={() => reorderPrograms(draggedProgramIndex, index)}
                          onDragEnd={() => setDraggedProgramIndex(null)}
                          onClick={() => {
                            setActiveProgramIndex(index);
                            setOpenProgramPanel(null);
                            setEditingScheduleIndex(null);
                            setEditingVenueIndex(null);
                          }}
                        >
                          <div>
                            <strong>{program.title || "Untitled Program"}</strong>
                            <p>{getProgramSummary(program)}</p>

                            <button
                              type="button"
                              className="admin-program-remove"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteProgram(index);
                              }}
                            >
                              Remove
                            </button>
                          </div>

                          <span>›</span>
                        </div>
                      ))}
                      {programs.length > 1 && (
                        <p className="admin-program-reorder-note">
                          Drag programs to reorder how they appear on the listing.
                        </p>
                      )}
                    </div>

                    {activeProgram &&
                      (() => {
                        const program = activeProgram;
                        const index = activeProgramIndex;

                        return (
                          <div className="admin-program-editor">
                            <div className="admin-program-card">
                              <div className="admin-field-group">
                                <label className="admin-field-label">Program Title</label>
                                <input
                                  type="text"
                                  placeholder="Ex: Adult Basketball League"
                                  value={program.title || ""}
                                  onChange={(e) =>
                                    updateProgramField(index, "title", e.target.value)
                                  }
                                />
                              </div>

                              <div className="admin-field-group">
                                <label className="admin-field-label">Sport</label>
                                <select
                                  value={program.sport}
                                  onChange={(e) =>
                                    updateProgramField(index, "sport", e.target.value)
                                  }
                                >
                                  <option value="">Select Sport</option>
                                  {SPORTS.map((sport) => (
                                    <option key={sport.key} value={sport.key}>
                                      {sport.label}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div className="admin-field-group">
                                <label className="admin-field-label">Program Type</label>
                                <select
                                  value={program.program_type}
                                  onChange={(e) =>
                                    updateProgramField(index, "program_type", e.target.value)
                                  }
                                >
                                  <option value="">Select Program Type</option>
                                  {PROGRAM_TYPES.map((type) => (
                                    <option key={type.key} value={type.key}>
                                      {type.label}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div className="admin-field-group">
                                <label className="admin-field-label">Competition Level</label>
                                <select
                                  value={program.competition_level || ""}
                                  onChange={(e) =>
                                    updateProgramField(
                                      index,
                                      "competition_level",
                                      e.target.value
                                    )
                                  }
                                >
                                  <option value="">Select Competition Level</option>
                                  {COMPETITION_LEVELS.map((level) => (
                                    <option key={level.key} value={level.key}>
                                      {level.label}
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div className="admin-program-audience-block">
                                <div className="admin-field-label">Audience</div>

                                <div className="admin-program-audience-options">
                                  {AUDIENCES.map((option) => {
                                    const checked = (program.gender || []).includes(
                                      option.key
                                    );

                                    return (
                                      <label
                                        key={option.key}
                                        className={`admin-audience-pill ${checked ? "active" : ""
                                          }`}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          onChange={(e) => {
                                            const current = Array.isArray(program.gender)
                                              ? program.gender
                                              : [];

                                            const nextGender = e.target.checked
                                              ? [...current, option.key]
                                              : current.filter((g) => g !== option.key);

                                            updateProgramField(index, "gender", nextGender);
                                          }}
                                        />
                                        <span>{option.label}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>

                              <div className="admin-field-group">
                                <label className="admin-field-label">Minimum Age</label>
                                <input
                                  type="number"
                                  placeholder="Ex: 18"
                                  value={program.min_age || ""}
                                  onChange={(e) =>
                                    updateProgramField(index, "min_age", e.target.value)
                                  }
                                />
                              </div>

                              <div className="admin-field-group">
                                <label className="admin-field-label">Maximum Age</label>
                                <input
                                  type="number"
                                  placeholder="Leave blank for 18+"
                                  value={program.max_age || ""}
                                  onChange={(e) =>
                                    updateProgramField(index, "max_age", e.target.value)
                                  }
                                />
                              </div>

                              <div className="admin-field-group admin-full-width">
                                <label className="admin-field-label">
                                  Program Description
                                </label>
                                <textarea
                                  placeholder="Briefly describe this program"
                                  value={program.description || ""}
                                  onChange={(e) =>
                                    updateProgramField(index, "description", e.target.value)
                                  }
                                />
                              </div>
                              <div className="admin-program-panel">
                                <button
                                  type="button"
                                  className="admin-program-panel-header"
                                  onClick={() => toggleProgramPanel("contact")}
                                >
                                  <div>
                                    <strong>Program Contact</strong>
                                    <p>Who users should contact about this program</p>
                                  </div>

                                  <span>{openProgramPanel === "contact" ? "▲" : "▼"}</span>
                                </button>

                                {openProgramPanel === "contact" && (
                                  <div className="admin-program-panel-body">
                                    <label className="admin-field-label">Contact Name</label>
                                    <input
                                      type="text"
                                      placeholder="Ex: Coach Mike, Program Director"
                                      value={program.contact_name || ""}
                                      onChange={(e) =>
                                        updateProgramField(index, "contact_name", e.target.value)
                                      }
                                    />

                                    <label className="admin-field-label">Contact Email</label>
                                    <input
                                      type="email"
                                      placeholder="program@example.com"
                                      value={program.email || ""}
                                      onChange={(e) => updateProgramField(index, "email", e.target.value)}
                                    />

                                    <label className="admin-field-label">Contact Phone</label>
                                    <input
                                      type="text"
                                      placeholder="(555) 555-5555"
                                      value={program.phone_number || ""}
                                      onChange={(e) =>
                                        updateProgramField(index, "phone_number", e.target.value)
                                      }
                                    />

                                    <label className="admin-field-label">Contact Note</label>
                                    <textarea
                                      placeholder="Ex: Text preferred, ask for Coach Mike, call ext. 204"
                                      value={program.contact_note || ""}
                                      onChange={(e) =>
                                        updateProgramField(index, "contact_note", e.target.value)
                                      }
                                    />
                                  </div>
                                )}
                              </div>
                              <div className="admin-program-panel">
                                <button
                                  type="button"
                                  className="admin-program-panel-header"
                                  onClick={() => toggleProgramPanel("schedule")}
                                >
                                  <div>
                                    <strong>Schedule</strong>
                                    <p>Days, times, and labels for this program</p>
                                  </div>

                                  <span>{openProgramPanel === "schedule" ? "▲" : "▼"}</span>
                                </button>

                                {openProgramPanel === "schedule" && (
                                  <div className="admin-program-panel-body">
                                    {(program.schedule_details || []).map((schedule, sIndex) => {
                                      const isEditingSchedule = editingScheduleIndex === sIndex;

                                      return (
                                        <div
                                          key={sIndex}
                                          className={isEditingSchedule ? "admin-schedule-row" : "admin-mini-card"}
                                        >
                                          {!isEditingSchedule ? (
                                            <>
                                              <div>
                                                <strong>{formatScheduleSummary(schedule)}</strong>
                                                <p>{schedule.label || "No label added"}</p>
                                              </div>

                                              <div className="admin-mini-card-actions">
                                                <button
                                                  type="button"
                                                  className="admin-small-action"
                                                  onClick={() => setEditingScheduleIndex(sIndex)}
                                                >
                                                  Edit
                                                </button>

                                                <button
                                                  type="button"
                                                  className="admin-small-danger"
                                                  onClick={() => {
                                                    const confirmed = window.confirm("Remove this schedule?");
                                                    if (!confirmed) return;

                                                    const next = [...programs];

                                                    next[index] = {
                                                      ...next[index],
                                                      schedule_details: (next[index].schedule_details || []).filter(
                                                        (_, i) => i !== sIndex
                                                      ),
                                                    };

                                                    setPrograms(next);
                                                    setEditingScheduleIndex(null);
                                                  }}
                                                >
                                                  Remove
                                                </button>
                                              </div>
                                            </>
                                          ) : (
                                            <>
                                              <div className="admin-field-group">
                                                <label className="admin-field-label">Start Time</label>
                                                <input
                                                  type="time"
                                                  value={schedule.start_time || ""}
                                                  onChange={(e) => {
                                                    const next = [...programs];
                                                    const schedules = [...(next[index].schedule_details || [])];

                                                    schedules[sIndex] = {
                                                      ...schedules[sIndex],
                                                      start_time: e.target.value,
                                                    };

                                                    next[index] = {
                                                      ...next[index],
                                                      schedule_details: schedules,
                                                    };

                                                    setPrograms(next);
                                                  }}
                                                />
                                              </div>

                                              <div className="admin-field-group">
                                                <label className="admin-field-label">End Time</label>
                                                <input
                                                  type="time"
                                                  value={schedule.end_time || ""}
                                                  onChange={(e) => {
                                                    const next = [...programs];
                                                    const schedules = [...(next[index].schedule_details || [])];

                                                    schedules[sIndex] = {
                                                      ...schedules[sIndex],
                                                      end_time: e.target.value,
                                                    };

                                                    next[index] = {
                                                      ...next[index],
                                                      schedule_details: schedules,
                                                    };

                                                    setPrograms(next);
                                                  }}
                                                />
                                              </div>

                                              <div className="admin-field-group admin-schedule-days-field">
                                                <label className="admin-field-label">Days</label>

                                                <div className="admin-day-options">
                                                  {[
                                                    "Monday",
                                                    "Tuesday",
                                                    "Wednesday",
                                                    "Thursday",
                                                    "Friday",
                                                    "Saturday",
                                                    "Sunday",
                                                  ].map((day) => {
                                                    const selectedDays = Array.isArray(schedule.days)
                                                      ? schedule.days
                                                      : schedule.day
                                                        ? [schedule.day]
                                                        : [];

                                                    const checked = selectedDays.includes(day);

                                                    return (
                                                      <label
                                                        key={day}
                                                        className={`admin-day-pill ${checked ? "active" : ""}`}
                                                      >
                                                        <input
                                                          type="checkbox"
                                                          checked={checked}
                                                          onChange={(e) => {
                                                            const next = [...programs];
                                                            const schedules = [
                                                              ...(next[index].schedule_details || []),
                                                            ];

                                                            const currentDays = Array.isArray(
                                                              schedules[sIndex].days
                                                            )
                                                              ? schedules[sIndex].days
                                                              : schedules[sIndex].day
                                                                ? [schedules[sIndex].day]
                                                                : [];

                                                            const nextDays = e.target.checked
                                                              ? [...currentDays, day]
                                                              : currentDays.filter((d) => d !== day);

                                                            schedules[sIndex] = {
                                                              ...schedules[sIndex],
                                                              day: undefined,
                                                              days: nextDays,
                                                            };

                                                            next[index] = {
                                                              ...next[index],
                                                              schedule_details: schedules,
                                                            };

                                                            setPrograms(next);
                                                          }}
                                                        />

                                                        <span>{day.slice(0, 3)}</span>
                                                      </label>
                                                    );
                                                  })}
                                                </div>
                                              </div>

                                              <div className="admin-field-group admin-schedule-label-field">
                                                <label className="admin-field-label">Schedule Label</label>
                                                <input
                                                  placeholder="Ex: Private Lesson, Open Play, Beginner Class"
                                                  value={schedule.label || ""}
                                                  onChange={(e) => {
                                                    const next = [...programs];
                                                    const schedules = [...(next[index].schedule_details || [])];

                                                    schedules[sIndex] = {
                                                      ...schedules[sIndex],
                                                      label: e.target.value,
                                                    };

                                                    next[index] = {
                                                      ...next[index],
                                                      schedule_details: schedules,
                                                    };

                                                    setPrograms(next);
                                                  }}
                                                />
                                              </div>

                                              <div className="admin-edit-actions">
                                                <button
                                                  type="button"
                                                  className="admin-small-action"
                                                  onClick={() => setEditingScheduleIndex(null)}
                                                >
                                                  Done
                                                </button>

                                                <button
                                                  type="button"
                                                  className="admin-small-danger"
                                                  onClick={() => {
                                                    const confirmed = window.confirm("Remove this schedule?");
                                                    if (!confirmed) return;

                                                    const next = [...programs];

                                                    next[index] = {
                                                      ...next[index],
                                                      schedule_details: (next[index].schedule_details || []).filter(
                                                        (_, i) => i !== sIndex
                                                      ),
                                                    };

                                                    setPrograms(next);
                                                    setEditingScheduleIndex(null);
                                                  }}
                                                >
                                                  Remove
                                                </button>
                                              </div>
                                            </>
                                          )}
                                        </div>
                                      );
                                    })}

                                    <button
                                      type="button"
                                      className="btn-secondary directory-small-btn"
                                      onClick={() => {
                                        const next = [...programs];

                                        const nextSchedules = [
                                          ...(next[index].schedule_details || []),
                                          {
                                            days: [],
                                            start_time: "",
                                            end_time: "",
                                            label: "",
                                          },
                                        ];

                                        next[index] = {
                                          ...next[index],
                                          schedule_details: nextSchedules,
                                        };

                                        setPrograms(next);
                                        setEditingScheduleIndex(nextSchedules.length - 1);
                                      }}
                                    >
                                      + Add Schedule
                                    </button>
                                  </div>
                                )}
                              </div>
                              <div className="admin-program-panel">
                                <button
                                  type="button"
                                  className="admin-program-panel-header"
                                  onClick={() => toggleProgramPanel("venues")}
                                >
                                  <div>
                                    <strong>Locations / Venues</strong>
                                    <p>Add where this program takes place</p>
                                  </div>

                                  <span>{openProgramPanel === "venues" ? "▲" : "▼"}</span>
                                </button>

                                {openProgramPanel === "venues" && (
                                  <div className="admin-program-panel-body">
                                    {(program.venues || []).map((venue, vIndex) => {
                                      const isEditingVenue = editingVenueIndex === vIndex;

                                      return (
                                        <div
                                          key={vIndex}
                                          className={isEditingVenue ? "admin-venue-row" : "admin-mini-card"}
                                        >
                                          {!isEditingVenue ? (
                                            <>
                                              <div>
                                                <strong>{venue.name || "Untitled venue"}</strong>
                                                {venue.map_url && <p>Map link added</p>}
                                              </div>

                                              <div className="admin-mini-card-actions">
                                                <button
                                                  type="button"
                                                  className="admin-small-action"
                                                  onClick={() => setEditingVenueIndex(vIndex)}
                                                >
                                                  Edit
                                                </button>

                                                <button
                                                  type="button"
                                                  className="admin-small-danger"
                                                  onClick={() => {
                                                    const confirmed = window.confirm("Remove this venue?");
                                                    if (!confirmed) return;

                                                    const next = [...programs];

                                                    next[index] = {
                                                      ...next[index],
                                                      venues: (next[index].venues || []).filter(
                                                        (_, i) => i !== vIndex
                                                      ),
                                                    };

                                                    setPrograms(next);
                                                    setEditingVenueIndex(null);
                                                  }}
                                                >
                                                  Remove
                                                </button>
                                              </div>
                                            </>
                                          ) : (
                                            <>
                                              <div className="admin-field-group">
                                                <label className="admin-field-label">Venue Name</label>
                                                <input
                                                  type="text"
                                                  placeholder="Ex: Tustin Rec Center"
                                                  value={venue.name || ""}
                                                  onChange={(e) => {
                                                    const next = [...programs];
                                                    const venues = [...(next[index].venues || [])];

                                                    venues[vIndex] = {
                                                      ...venues[vIndex],
                                                      name: e.target.value,
                                                    };

                                                    next[index] = {
                                                      ...next[index],
                                                      venues,
                                                    };

                                                    setPrograms(next);
                                                  }}
                                                />
                                              </div>

                                              <div className="admin-field-group">
                                                <label className="admin-field-label">Google Map URL</label>
                                                <input
                                                  type="text"
                                                  placeholder="Optional Google Map link"
                                                  value={venue.map_url || ""}
                                                  onChange={(e) => {
                                                    const next = [...programs];
                                                    const venues = [...(next[index].venues || [])];

                                                    venues[vIndex] = {
                                                      ...venues[vIndex],
                                                      map_url: e.target.value,
                                                    };

                                                    next[index] = {
                                                      ...next[index],
                                                      venues,
                                                    };

                                                    setPrograms(next);
                                                  }}
                                                />
                                              </div>

                                              <div className="admin-edit-actions">
                                                <button
                                                  type="button"
                                                  className="admin-small-action"
                                                  onClick={() => setEditingVenueIndex(null)}
                                                >
                                                  Done
                                                </button>

                                                <button
                                                  type="button"
                                                  className="admin-small-danger"
                                                  onClick={() => {
                                                    const confirmed = window.confirm("Remove this venue?");
                                                    if (!confirmed) return;

                                                    const next = [...programs];

                                                    next[index] = {
                                                      ...next[index],
                                                      venues: (next[index].venues || []).filter(
                                                        (_, i) => i !== vIndex
                                                      ),
                                                    };

                                                    setPrograms(next);
                                                    setEditingVenueIndex(null);
                                                  }}
                                                >
                                                  Remove
                                                </button>
                                              </div>
                                            </>
                                          )}
                                        </div>
                                      );
                                    })}

                                    <button
                                      type="button"
                                      className="btn-secondary directory-small-btn"
                                      onClick={() => {
                                        const next = [...programs];

                                        const nextVenues = [
                                          ...(next[index].venues || []),
                                          { name: "", map_url: "" },
                                        ];

                                        next[index] = {
                                          ...next[index],
                                          venues: nextVenues,
                                        };

                                        setPrograms(next);
                                        setEditingVenueIndex(nextVenues.length - 1);
                                      }}
                                    >
                                      + Add Venue
                                    </button>
                                  </div>
                                )}
                              </div>

                              <div className="admin-program-panel">
                                <button
                                  type="button"
                                  className="admin-program-panel-header"
                                  onClick={() => toggleProgramPanel("registration")}
                                >
                                  <div>
                                    <strong>Registration / Booking</strong>
                                    <p>Price, booking link, status, and dates</p>
                                  </div>

                                  <span>{openProgramPanel === "registration" ? "▲" : "▼"}</span>
                                </button>

                                {openProgramPanel === "registration" && (
                                  <div className="admin-program-panel-body">
                                    <label className="admin-field-label">Price / Fee</label>
                                    <input
                                      type="text"
                                      placeholder="Ex: $40 per lesson, Free, Contact for pricing"
                                      value={program.price || ""}
                                      onChange={(e) =>
                                        updateProgramField(index, "price", e.target.value)
                                      }
                                    />

                                    <label className="admin-field-label">Registration / Booking URL</label>
                                    <input
                                      type="text"
                                      placeholder="https://..."
                                      value={program.registration_url || ""}
                                      onChange={(e) =>
                                        updateProgramField(index, "registration_url", e.target.value)
                                      }
                                    />

                                    <label className="admin-field-label">Registration Status</label>
                                    <input
                                      type="text"
                                      placeholder="Ex: Open, Closed, Waitlist"
                                      value={program.registration_status || ""}
                                      onChange={(e) =>
                                        updateProgramField(index, "registration_status", e.target.value)
                                      }
                                    />

                                    <label className="admin-field-label">Registration Deadline</label>
                                    <input
                                      type="date"
                                      value={program.registration_deadline || ""}
                                      onChange={(e) =>
                                        updateProgramField(index, "registration_deadline", e.target.value)
                                      }
                                    />

                                    <label className="admin-field-label">Program Start Date</label>
                                    <input
                                      type="date"
                                      value={program.start_date || ""}
                                      onChange={(e) =>
                                        updateProgramField(index, "start_date", e.target.value)
                                      }
                                    />

                                    <label className="admin-field-label">Program End Date</label>
                                    <input
                                      type="date"
                                      value={program.end_date || ""}
                                      onChange={(e) =>
                                        updateProgramField(index, "end_date", e.target.value)
                                      }
                                    />

                                    <label className="admin-field-label">Season</label>
                                    <input
                                      type="text"
                                      placeholder="Ex: Spring 2026, Summer Camp"
                                      value={program.season || ""}
                                      onChange={(e) =>
                                        updateProgramField(index, "season", e.target.value)
                                      }
                                    />

                                    <label className="admin-field-label">Program Notes</label>
                                    <textarea
                                      placeholder="Anything users should know before registering"
                                      value={program.notes || ""}
                                      onChange={(e) =>
                                        updateProgramField(index, "notes", e.target.value)
                                      }
                                    />
                                  </div>
                                )}
                              </div>

                            </div>
                          </div>
                        );
                      })()}
                  </div>
                </div>
              )}
            </div>

            {/* ACTIONS */}
            <div className="modal-actions">
              <button
                className="btn-primary"
                onClick={async () => {
                  try {
                    for (const p of programs) {
                      if (!p.sport || !p.program_type) {
                        setAlertModal({
                          type: "error",
                          message: "Please complete Sport and Program Type for all programs.",
                        });
                        return;
                      }
                    }
                    const formData = new FormData();

                    formData.append("name", form.name || "");
                    formData.append("contact_name", form.contact_name || "");
                    formData.append("country", form.country || "US");
                    formData.append("city", form.city || "");
                    formData.append("county", form.county || "");
                    formData.append("state", form.state || "");
                    formData.append("phone_number", form.phone_number || "");
                    formData.append("website_url", form.website_url || "");
                    formData.append("register_url", form.register_url || "");
                    formData.append("register_label", form.register_label || "");
                    formData.append("instagram_url", form.instagram_url || "");
                    formData.append("facebook_url", form.facebook_url || "");
                    formData.append("youtube_url", form.youtube_url || "");
                    formData.append("twitter_url", form.twitter_url || "");
                    formData.append("email", form.email || "");
                    formData.append("notes", form.notes || "");
                    formData.append("description", form.description || "");
                    formData.append("is_active", form.is_active);
                    formData.append("is_verified", form.is_verified);

                    formData.append(
                      "locations",
                      JSON.stringify(
                        locationsInput
                          .split(",")
                          .map((c) => c.trim())
                          .filter(Boolean)
                      )
                    );
                    formData.append(
                      "venues",
                      JSON.stringify(
                        (form.venues || []).filter((venue) => venue.name?.trim())
                      )
                    );
                    formData.append("programs", JSON.stringify(programs));
                    formData.append(
                      "existing_gallery_images",
                      JSON.stringify(form.existing_gallery_images || [])
                    );
                    formData.append(
                      "existing_directory_logo",
                      form.existing_directory_logo || ""
                    );
                    if (form.directory_logo) {
                      formData.append("directory_logo", form.directory_logo);
                    }

                    if (form.gallery_images?.length) {
                      form.gallery_images.forEach((file) => {
                        formData.append("gallery_images", file);
                      });
                    }

                    if (editingLeague) {
                      await api.put(
                        `/api/admin/directory/leagues/${editingLeague.id}`,
                        formData,
                        { withCredentials: true }
                      );
                    } else {
                      await api.post(
                        "/api/admin/directory/leagues",
                        formData,
                        { withCredentials: true }
                      );
                    }

                    setAlertModal({
                      type: "success",
                      message: editingLeague
                        ? "League updated successfully."
                        : "League created successfully.",
                    });

                    setShowModal(false);
                    fetchLeagues();
                  } catch (err) {
                    console.error(err);
                    setAlertModal({
                      type: "error",
                      message: "Something went wrong.",
                    });
                  }
                }}
              >
                Save
              </button>
              <button
                className="btn-secondary"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}






    </div>
  );
}

export default DirectoryLeagues;
